import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { useModal } from '../../context/ModalContext';
import api from '../../utils/api';
import { 
  FiPlus, FiEdit, FiTrash2, FiSearch, FiUsers, FiPhone, FiMail, 
  FiClock, FiBell, FiCheckCircle, FiXCircle, FiAlertCircle, FiMapPin,
  FiRefreshCw, FiX
} from 'react-icons/fi';

function Waitlist() {
  const { alert, confirm } = useModal();
  const [waitlist, setWaitlist] = useState([]);
  const [tables, setTables] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingEntry, setEditingEntry] = useState(null);
  const [notifyingEntry, setNotifyingEntry] = useState(null);
  const [seatingEntry, setSeatingEntry] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [savingEntry, setSavingEntry] = useState(false);
  const [cancellingEntryId, setCancellingEntryId] = useState(null);
  const [seatingInProgress, setSeatingInProgress] = useState(false);
  const [formData, setFormData] = useState({
    customer_name: '',
    customer_phone: '',
    customer_email: '',
    party_size: '',
    preferred_table_id: '',
    special_requests: ''
  });
  const [selectedTableForSeating, setSelectedTableForSeating] = useState('');

  useEffect(() => {
    fetchWaitlist();
    fetchTables();
    fetchStats();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      fetchWaitlist();
      fetchStats();
    }, 30000);

    return () => clearInterval(interval);
  }, [statusFilter]);

  const fetchWaitlist = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status', statusFilter);
      
      const response = await api.get(`/api/waitlist?${params.toString()}`);
      setWaitlist(response.data || []);
    } catch (error) {
      console.error('Failed to fetch waitlist:', error);
      await alert('Failed to fetch waitlist', 'Error', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchTables = async () => {
    try {
      const response = await api.get('/api/tables');
      setTables(response.data || []);
    } catch (error) {
      console.error('Failed to fetch tables:', error);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/waitlist/stats');
      setStats(response.data || {});
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (savingEntry) {
      return;
    }

    setSavingEntry(true);
    try {
      const submitData = {
        ...formData,
        party_size: parseInt(formData.party_size),
        preferred_table_id: formData.preferred_table_id || null
      };

      if (editingEntry) {
        await api.put(`/api/waitlist/${editingEntry.id}`, submitData);
      } else {
        await api.post('/api/waitlist', submitData);
      }
      
      fetchWaitlist();
      fetchStats();
      setShowModal(false);
      resetForm();
    } catch (error) {
      console.error('Failed to save waitlist entry:', error);
      const errorMessage = error.response?.data?.error || 'Failed to save waitlist entry';
      await alert(errorMessage, 'Error', 'error');
    } finally {
      setSavingEntry(false);
    }
  };

  const handleNotify = async (entry) => {
    if (!entry?.id || notifyingEntry?.id === entry.id) {
      return;
    }

    setNotifyingEntry(entry);
    const previousWaitlist = waitlist;
    setWaitlist((prev) => prev.map((item) => (
      item.id === entry.id
        ? { ...item, status: 'notified', notified_at: new Date().toISOString() }
        : item
    )));

    try {
      const tableId = entry.preferred_table_id || null;
      await api.patch(`/api/waitlist/${entry.id}/notify`, { table_id: tableId });
      fetchWaitlist();
      fetchStats();
      setNotifyingEntry(null);
      await alert('Customer has been notified!', 'Success', 'success');
    } catch (error) {
      console.error('Failed to notify customer:', error);
      setWaitlist(previousWaitlist);
      await alert('Failed to notify customer', 'Error', 'error');
      setNotifyingEntry(null);
    }
  };

  const handleSeat = async () => {
    if (!selectedTableForSeating || !seatingEntry || seatingInProgress) return;

    setSeatingInProgress(true);
    const currentSeatingId = seatingEntry.id;
    const previousWaitlist = waitlist;
    setWaitlist((prev) => prev.map((item) => (
      item.id === currentSeatingId
        ? { ...item, status: 'seated' }
        : item
    )));
    
    try {
      await api.patch(`/api/waitlist/${currentSeatingId}/seat`, { 
        table_id: selectedTableForSeating 
      });
      fetchWaitlist();
      fetchStats();
      setSeatingEntry(null);
      setSelectedTableForSeating('');
      await alert('Customer has been seated!', 'Success', 'success');
    } catch (error) {
      console.error('Failed to seat customer:', error);
      setWaitlist(previousWaitlist);
      await alert('Failed to seat customer', 'Error', 'error');
    } finally {
      setSeatingInProgress(false);
    }
  };

  const handleCancel = async (id) => {
    if (!id || cancellingEntryId === id) {
      return;
    }

    const confirmed = await confirm('Are you sure you want to remove this entry from the waitlist?', 'Remove Entry', 'warning');
    if (!confirmed) return;

    setCancellingEntryId(id);
    const previousWaitlist = waitlist;
    setWaitlist((prev) => prev.map((item) => (
      item.id === id
        ? { ...item, status: 'cancelled' }
        : item
    )));
    
    try {
      await api.patch(`/api/waitlist/${id}/cancel`, {});
      fetchWaitlist();
      fetchStats();
    } catch (error) {
      console.error('Failed to cancel waitlist entry:', error);
      setWaitlist(previousWaitlist);
      await alert('Failed to cancel waitlist entry', 'Error', 'error');
    } finally {
      setCancellingEntryId(null);
    }
  };

  const resetForm = () => {
    setFormData({
      customer_name: '',
      customer_phone: '',
      customer_email: '',
      party_size: '',
      preferred_table_id: '',
      special_requests: ''
    });
    setEditingEntry(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'waiting': return 'bg-yellow-100 text-yellow-800';
      case 'notified': return 'bg-blue-100 text-blue-800';
      case 'seated': return 'bg-green-100 text-green-800';
      case 'cancelled': return 'bg-red-100 text-red-800';
      case 'no_show': return 'bg-orange-100 text-orange-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredWaitlist = waitlist.filter(entry => {
    const matchesSearch = 
      entry.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      entry.customer_phone?.includes(searchTerm);
    return matchesSearch;
  });

  // Get available tables for seating
  const availableTables = tables.filter(t => 
    t.status === 'available' || t.status === 'reserved'
  );

  if (loading) {
    return <Layout><div>Loading...</div></Layout>;
  }

  return (
    <Layout>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-1">Waitlist</h1>
        <p className="text-sm text-gray-600">Manage customer waitlist queue</p>
      </div>

      {/* Search and Filter Bar */}
      <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 mb-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
            <input
              type="text"
              placeholder="Search by name or phone..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
            />
          </div>
          <div className="flex items-center gap-3">
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm bg-white"
            >
              <option value="all">All Status</option>
              <option value="waiting">Waiting</option>
              <option value="notified">Notified</option>
              <option value="seated">Seated</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <button
              onClick={() => {
                fetchWaitlist();
                fetchStats();
              }}
              className="px-3 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center gap-2 text-sm transition-colors"
            >
              <FiRefreshCw className="h-4 w-4" />
            </button>
            <button
              onClick={() => {
                resetForm();
                setShowModal(true);
              }}
              className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800 flex items-center gap-2 text-sm transition-colors"
            >
              <FiPlus className="h-4 w-4" /> Add
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="bg-white rounded-lg border-0 shadow-sm p-4">
          <div className="text-xs text-gray-600 mb-1">Total</div>
          <div className="text-xl font-bold text-gray-900">{stats.total || 0}</div>
        </div>
        <div className="bg-white rounded-lg border-0 shadow-sm p-4">
          <div className="text-xs text-gray-600 mb-1">Waiting</div>
          <div className="text-xl font-bold text-yellow-600">{stats.waiting || 0}</div>
        </div>
        <div className="bg-white rounded-lg border-0 shadow-sm p-4">
          <div className="text-xs text-gray-600 mb-1">Notified</div>
          <div className="text-xl font-bold text-blue-600">{stats.notified || 0}</div>
        </div>
        <div className="bg-white rounded-lg border-0 shadow-sm p-4">
          <div className="text-xs text-gray-600 mb-1">Avg Wait Time</div>
          <div className="text-xl font-bold text-gray-900">{stats.average_wait_time || 0}m</div>
        </div>
        <div className="bg-white rounded-lg border-0 shadow-sm p-4">
          <div className="text-xs text-gray-600 mb-1">Avg Party Size</div>
          <div className="text-xl font-bold text-gray-900">{stats.average_party_size || 0}</div>
        </div>
      </div>

      {/* Waitlist Entries */}
      {filteredWaitlist.length === 0 ? (
        <div className="bg-white rounded-lg border-0 shadow-sm p-12 text-center">
          <FiUsers className="text-4xl text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No waitlist entries</p>
          <p className="text-gray-400 text-sm mt-2">
            {searchTerm || statusFilter !== 'all' 
              ? 'Try adjusting your filters' 
              : 'Add customers to the waitlist to get started'}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredWaitlist.map((entry) => (
            <div
              key={entry.id}
              className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 hover:shadow-lg hover:scale-[1.01] transition-all duration-200"
            >
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-gray-900 text-white rounded-full w-8 h-8 flex items-center justify-center font-bold text-sm flex-shrink-0">
                      {entry.queue_position}
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">{entry.customer_name}</h3>
                    <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusColor(entry.status)}`}>
                      {entry.status.toUpperCase()}
                    </span>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2 text-xs text-gray-600">
                    <div className="flex items-center gap-2">
                      <FiUsers className="text-gray-400 h-3 w-3" />
                      <span>{entry.party_size} guests</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FiClock className="text-gray-400 h-3 w-3" />
                      <span>Est. wait: {entry.estimated_wait_time || 0} min</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <FiPhone className="text-gray-400 h-3 w-3" />
                      <span>{entry.customer_phone}</span>
                    </div>
                    {entry.customer_email && (
                      <div className="flex items-center gap-2">
                        <FiMail className="text-gray-400 h-3 w-3" />
                        <span className="truncate">{entry.customer_email}</span>
                      </div>
                    )}
                    {entry.tables && (
                      <div className="flex items-center gap-2">
                        <FiMapPin className="text-gray-400 h-3 w-3" />
                        <span>Pref: Table {entry.tables.table_number}</span>
                      </div>
                    )}
                    {entry.notified_at && (
                      <div className="flex items-center gap-2">
                        <FiBell className="text-gray-400 h-3 w-3" />
                        <span>Notified: {new Date(entry.notified_at).toLocaleTimeString()}</span>
                      </div>
                    )}
                  </div>

                  {entry.special_requests && (
                    <div className="mt-3 p-2.5 bg-gray-50 rounded-lg">
                      <p className="text-xs text-gray-700">
                        <strong>Special Requests:</strong> {entry.special_requests}
                      </p>
                    </div>
                  )}
                </div>

                <div className="flex flex-row md:flex-col gap-2">
                  {entry.status === 'waiting' && (
                    <>
                      <button
                        onClick={() => handleNotify(entry)}
                        disabled={notifyingEntry?.id === entry.id}
                        className="px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center justify-center gap-2 text-xs transition-colors disabled:opacity-50"
                      >
                        <FiBell className="h-3 w-3" /> {notifyingEntry?.id === entry.id ? 'Notifying...' : 'Notify'}
                      </button>
                      <button
                        onClick={() => setSeatingEntry(entry)}
                        className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 text-xs transition-colors"
                      >
                        <FiCheckCircle className="h-3 w-3" /> Seat
                      </button>
                    </>
                  )}
                  {entry.status === 'notified' && (
                    <button
                      onClick={() => setSeatingEntry(entry)}
                      className="px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center justify-center gap-2 text-xs transition-colors"
                    >
                      <FiCheckCircle className="h-3 w-3" /> Seat
                    </button>
                  )}
                  <button
                    onClick={() => {
                      setEditingEntry(entry);
                      setFormData({
                        customer_name: entry.customer_name,
                        customer_phone: entry.customer_phone,
                        customer_email: entry.customer_email || '',
                        party_size: entry.party_size.toString(),
                        preferred_table_id: entry.preferred_table_id || '',
                        special_requests: entry.special_requests || ''
                      });
                      setShowModal(true);
                    }}
                    className="px-2.5 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 flex items-center justify-center gap-2 text-xs transition-colors"
                    title="Edit"
                  >
                    <FiEdit className="h-3 w-3" />
                  </button>
                  {entry.status !== 'seated' && entry.status !== 'cancelled' && (
                    <button
                      onClick={() => handleCancel(entry.id)}
                      disabled={cancellingEntryId === entry.id}
                      className="px-2.5 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 flex items-center justify-center gap-2 text-xs transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                      title="Remove"
                    >
                      <FiXCircle className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Add/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => {
          setShowModal(false);
          resetForm();
        }}>
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">
                {editingEntry ? 'Edit Waitlist Entry' : 'Add to Waitlist'}
              </h2>
              <button
                onClick={() => {
                  setShowModal(false);
                  resetForm();
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX className="text-xl" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Customer Name *</label>
                  <input
                    type="text"
                    value={formData.customer_name}
                    onChange={(e) => setFormData({ ...formData, customer_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Phone *</label>
                  <input
                    type="tel"
                    value={formData.customer_phone}
                    onChange={(e) => setFormData({ ...formData, customer_phone: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={formData.customer_email}
                    onChange={(e) => setFormData({ ...formData, customer_email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Party Size *</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.party_size}
                    onChange={(e) => setFormData({ ...formData, party_size: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                    required
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Preferred Table (Optional)</label>
                  <select
                    value={formData.preferred_table_id}
                    onChange={(e) => setFormData({ ...formData, preferred_table_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                  >
                    <option value="">No preference</option>
                    {tables.map(table => (
                      <option key={table.id} value={table.id}>
                        Table {table.table_number} - Capacity: {table.capacity} ({table.location})
                      </option>
                    ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Special Requests</label>
                  <textarea
                    value={formData.special_requests}
                    onChange={(e) => setFormData({ ...formData, special_requests: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                    rows="2"
                  />
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="submit"
                  disabled={savingEntry}
                  className="flex-1 bg-gray-900 text-white py-2 rounded-lg hover:bg-gray-800 transition-colors text-sm disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {savingEntry ? 'Saving...' : `${editingEntry ? 'Update' : 'Add to'} Waitlist`}
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setShowModal(false);
                    resetForm();
                  }}
                  className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 transition-colors text-sm"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Seat Customer Modal */}
      {seatingEntry && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => {
          setSeatingEntry(null);
          setSelectedTableForSeating('');
        }}>
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-900">Seat Customer</h2>
              <button
                onClick={() => {
                  setSeatingEntry(null);
                  setSelectedTableForSeating('');
                }}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <FiX className="text-xl" />
              </button>
            </div>

            <div className="mb-4">
              <p className="text-sm text-gray-700 mb-3">
                <strong>{seatingEntry.customer_name}</strong> - Party of {seatingEntry.party_size}
              </p>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Select Table *</label>
              <select
                value={selectedTableForSeating}
                onChange={(e) => setSelectedTableForSeating(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-gray-900 focus:border-transparent text-sm"
                required
              >
                <option value="">Select a table</option>
                {availableTables.map(table => (
                  <option key={table.id} value={table.id}>
                    Table {table.table_number} - Capacity: {table.capacity} ({table.location}) - {table.status}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex gap-3">
              <button
                onClick={handleSeat}
                disabled={!selectedTableForSeating || seatingInProgress}
                className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
              >
                {seatingInProgress ? 'Seating...' : 'Seat Customer'}
              </button>
              <button
                onClick={() => {
                  setSeatingEntry(null);
                  setSelectedTableForSeating('');
                }}
                className="flex-1 bg-gray-100 text-gray-700 py-2 rounded-lg hover:bg-gray-200 transition-colors text-sm"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

export default Waitlist;



